import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { SOCIAL_LINKS } from '../constants';
import { useLocale } from '../i18n';
import { counterpart, homePath, sectionPath } from '../seo/routes';

interface Command {
  id: string;
  label: string;
  hint?: string;
  run: () => void;
}

export function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { t } = useTranslation();
  const locale = useLocale();
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen((o) => !o);
      } else if (e.key === 'Escape') {
        setOpen(false);
      }
    };
    const onOpen = () => setOpen(true);
    window.addEventListener('keydown', onKey);
    window.addEventListener('command-palette:open', onOpen);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('command-palette:open', onOpen);
    };
  }, []);

  useEffect(() => {
    if (open) {
      setQuery('');
      setActive(0);
      const id = setTimeout(() => inputRef.current?.focus(), 0);
      return () => clearTimeout(id);
    }
  }, [open]);

  const commands = useMemo<Command[]>(() => {
    const home = homePath(locale);
    const goSection = (hash: string) => window.location.assign(sectionPath(locale, hash));
    const nextLocale = locale === 'en' ? 'de' : 'en';
    return [
      { id: 'home', label: t('palette.home'), run: () => navigate(home, { viewTransition: true }) },
      { id: 'services', label: t('palette.services'), run: () => goSection('services') },
      { id: 'work', label: t('palette.work'), run: () => goSection('projects') },
      { id: 'about', label: t('palette.about'), run: () => goSection('about') },
      {
        id: 'lang',
        label: t('palette.language'),
        hint: t('palette.hintView'),
        run: () => window.location.assign(counterpart(pathname, nextLocale)),
      },
      { id: 'github', label: t('palette.github'), hint: t('palette.hintExternal'), run: () => window.open(SOCIAL_LINKS.github, '_blank') },
      { id: 'linkedin', label: t('palette.linkedin'), hint: t('palette.hintExternal'), run: () => window.open(SOCIAL_LINKS.linkedin, '_blank') },
      { id: 'contact', label: t('palette.contact'), hint: t('palette.hintForm'), run: () => goSection('contact') },
    ];
  }, [locale, navigate, pathname, t]);

  const filtered = useMemo(
    () => commands.filter((c) => c.label.toLowerCase().includes(query.toLowerCase())),
    [commands, query],
  );

  const exec = (c: Command | undefined) => {
    if (!c) return;
    c.run();
    setOpen(false);
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-start justify-center bg-black/50 p-4 pt-[12vh] backdrop-blur-sm"
      onClick={() => setOpen(false)}
    >
      <div
        className="w-full max-w-lg overflow-hidden clip-chamfer border border-border bg-surface-elevated shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <input
          ref={inputRef}
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setActive(0);
          }}
          onKeyDown={(e) => {
            if (e.key === 'ArrowDown') {
              e.preventDefault();
              setActive((a) => Math.min(a + 1, filtered.length - 1));
            } else if (e.key === 'ArrowUp') {
              e.preventDefault();
              setActive((a) => Math.max(a - 1, 0));
            } else if (e.key === 'Enter') {
              e.preventDefault();
              exec(filtered[active]);
            }
          }}
          placeholder={t('palette.placeholder')}
          aria-label={t('palette.placeholder')}
          className="w-full border-b border-border bg-transparent px-4 py-3 text-sm outline-none placeholder:text-muted-foreground"
        />
        <ul className="max-h-72 overflow-y-auto p-1">
          {filtered.length === 0 && (
            <li className="px-3 py-6 text-center text-sm text-muted-foreground">{t('palette.empty')}</li>
          )}
          {filtered.map((c, i) => (
            <li key={c.id}>
              <button
                type="button"
                onMouseEnter={() => setActive(i)}
                onClick={() => exec(c)}
                className={`flex w-full items-center justify-between clip-chamfer px-3 py-2 text-left text-sm transition-colors ${
                  i === active ? 'bg-primary/10 text-foreground' : 'text-muted-foreground'
                }`}
              >
                <span>{c.label}</span>
                {c.hint && (
                  <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                    {c.hint}
                  </span>
                )}
              </button>
            </li>
          ))}
        </ul>
        <div className="flex items-center justify-between border-t border-border px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
          <span>{t('palette.keys')}</span>
          <span>⌘K</span>
        </div>
      </div>
    </div>
  );
}
